// Storage key for tracking which related questions the user has seen this session
const STORAGE_KEY = 'dive_semantic_search_seen_questions';

/**
 * Render a single question item into the DOM.
 * Preserves current scope and sort parameters in the question URL.
 */
function renderQuestionItem(questionData, scope = '', sortMode = '') {
  const question = questionData.question || '';
  const justification = questionData.justification || '';

  let questionUrl = `/search/?q=${encodeURIComponent(question)}`;
  if (scope === 'site') {
    questionUrl += '&scope=site';
  }
  if (sortMode === 'date') {
    questionUrl += '&sort=date';
  }

  const $item = $(`
    <li class="semantic-search-paa__item">
      <a class="semantic-search-paa__link" href="${questionUrl}">
        <span class="semantic-search-paa__text"></span>
        <img
          class="semantic-search-paa__icon"
          src="/static/img/menu_icons/search.svg"
          alt=""
          width="18"
          height="18"
          loading="lazy"
        >
      </a>
    </li>
  `);

  // Set via .attr()/.text() (not string interpolation) so justification/question can't
  // break out of the markup regardless of quote/angle-bracket characters they contain.
  const $link = $item.find('.semantic-search-paa__link');
  if (justification) {
    $link.attr('title', justification);
  }
  $item.find('.semantic-search-paa__text').text(question);

  return $item;
}

/**
 * Normalize a question string for consistent comparison.
 * Converts to lowercase and trims whitespace.
 */
function normalize(question) {
  return String(question || '').toLowerCase().trim();
}

/**
 * Get the list of seen questions from sessionStorage.
 * Returns an empty array if storage is unavailable or empty.
 */
function getSeen() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    // sessionStorage unavailable (incognito mode, etc.) - return empty array
    return [];
  }
}

/**
 * Check if a question has been seen this session.
 */
function hasBeenSeen(question) {
  const normalized = normalize(question);
  return getSeen().includes(normalized);
}

/**
 * Clear all seen questions from sessionStorage.
 * Used when resetting the cycle after all questions exhausted.
 */
function clearSeenQuestions() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // Do nothing
  }
}

/**
 * Pick up to `count` not-yet-seen questions out of `allQuestions`. If fewer than
 * `count` remain unseen, resets the tracker and re-starts the cycle from the
 * beginning of `allQuestions` so a full slate can still be filled.
 * Shared by every surface that shows related questions (search page PAA sections,
 * nav dropdown) so a question shown once anywhere isn't shown again elsewhere.
 */
function selectUnseenQuestions(allQuestions, count) {
  let unseenQuestions = allQuestions.filter((q) => q.question && !hasBeenSeen(q.question));

  if (unseenQuestions.length < count) {
    clearSeenQuestions();
    unseenQuestions = allQuestions.filter((q) => q.question);
  }

  return unseenQuestions.slice(0, count);
}

function getQuestionsToShow() {
  // Read all questions from the data attribute
  const questionsJson = $('#js-related-questions-data').text();
  const allQuestions = JSON.parse(questionsJson);

  // display 3 questions on mobile and 5 on tablet/desktop
  const questionCountToDisplay = window.innerWidth > 640 ? 5 : 3;

  return selectUnseenQuestions(allQuestions, questionCountToDisplay);
}

let currentlyDisplayedQuestions = [];

function renderQuestionsToPaas(questionsToShow, scope = '', sortMode = '') {
  $('.semantic-search-paa').each(function() {
    const $section = $(this);
    const $list = $section.find('.semantic-search-paa__list').empty();

    if (questionsToShow.length === 0) {
      $section.hide();
    } else {
      $section.show();
    }

    questionsToShow.forEach((q) => {
      $list.append(renderQuestionItem(q, scope, sortMode));
    });
  });
}

/**
 * Mark a question as seen by adding it to sessionStorage.
 * Questions are normalized (lowercase, trimmed) before storing.
 */
function markAsSeen(question) {
  const normalized = normalize(question);
  if (!normalized) return;

  const seen = getSeen();
  if (!seen.includes(normalized)) {
    seen.push(normalized);
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(seen));
    } catch (e) {
      // Incognito or storage full - gracefully degrade, no error thrown
    }
  }
}

/**
 * Filter and render related questions for a specific People Also Ask section.
 * Shows 3 questions on mobile and 5 on desktop/tablet. If fewer than 5 unseen questions remain,
 * resets the tracker and fills the remaining slots with questions from the start.
 */
export default function filterSeenRelatedQuestions() {
  const questionsToShow = getQuestionsToShow();
  currentlyDisplayedQuestions = questionsToShow;

  // Build URL with current scope and sort parameters for initial render
  const currentUrl = new URL(window.location.href);
  const scope = currentUrl.searchParams.get('scope') || '';
  const sortMode = currentUrl.searchParams.get('sort') || '';

  renderQuestionsToPaas(questionsToShow, scope, sortMode);

  // Mark questions as seen
  questionsToShow.forEach((q) => {
    markAsSeen(q.question);
  });
}

/**
 * Re-render the same related questions into People Also Ask sections.
 * Used after scope/sort changes that swap result markup but keep the
 * underlying question set unchanged. Does not mark questions as seen again.
 */
export function reRenderRelatedQuestions(scope = '', sortMode = '') {
  renderQuestionsToPaas(currentlyDisplayedQuestions, scope, sortMode);
}

export {
  markAsSeen,
  selectUnseenQuestions,
};
